/** @ken/module-accounts-payable — frontend entry point.
 *
 *  0.3.0 — bill_payments foldin (mig 0170). Adds /bill-payments
 *  sub-list page + the Payments tab on the bill detail sheet.
 *
 *  0.4.0 — vendor_credits foldin (mig 0184). Adds /vendor-credits
 *  sub-list page + VendorCreditDetailSheet (three-query pattern per
 *  ADR-0071) for supplier-issued credits — the AP-side mirror of
 *  customer credit memos.
 *
 *  0.5.0 — story-page adoption (ADR-0081/0082). The in-list bill Sheet is
 *  RETIRED: a bill is a DOCUMENT archetype, so its detail is now a full-page
 *  story (supplier-bills/$id) with routed Overview / Line Items / Payments /
 *  Related / Notes tabs and the money HeroBanner. Mirrors invoicing.
 *
 *  0.6.0 — UI-08. The module's other two lists stop being dead ends.
 *
 *  `bill-payments` had no detail route and no detail component: the row click
 *  was a documented no-op, written into the PRD's own UI checklist. It has a
 *  story page now.
 *
 *  `vendor-credits` had the opposite problem — a FULLY BUILT detail sheet that
 *  nothing imported. A finished screen in the bundle with no way in. It also
 *  used the retired in-list Sheet pattern (ADR-0081), so giving it a route
 *  would have been shipping the superseded shape deliberately. It has a story
 *  page now and `components/vendor-credit-detail-sheet.tsx` is DELETED in the
 *  same change — shipping both is how a codebase ends up with two detail
 *  surfaces and no rule about which one is real (UI-08.1).
 *
 *  Both are the CONFIG archetype, read from each one's own
 *  canonical-field-layout response rather than assumed here.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { BillStorySearch } from "./routes/story/bill-story-detail";
import type { BillPaymentStorySearch } from "./routes/story/bill-payment-story-detail";
import type { VendorCreditStorySearch } from "./routes/story/vendor-credit-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadBillPaymentStoryTabs = () => import("./routes/story/bill-payment-story-tabs");
const loadBillStoryTabs = () => import("./routes/story/bill-story-tabs");
const loadVendorCreditStoryTabs = () => import("./routes/story/vendor-credit-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const BillEditPage = lazyScreen(
  () => import("./routes/bill-edit"),
  "BillEditPage",
);
const BillPaymentListPage = lazyScreen(
  () => import("./routes/bill-payment-list"),
  "BillPaymentListPage",
);
const SupplierBillCreatePage = lazyScreen(
  () => import("./routes/supplier-bill-create"),
  "SupplierBillCreatePage",
);
const SupplierBillListPage = lazyScreen(
  () => import("./routes/supplier-bill-list"),
  "SupplierBillListPage",
);
const BillStoryDetail = lazyScreen(
  () => import("./routes/story/bill-story-detail"),
  "BillStoryDetail",
);
const BillPaymentStoryDetail = lazyScreen(
  () => import("./routes/story/bill-payment-story-detail"),
  "BillPaymentStoryDetail",
);
const BillPaymentActivityRoute = lazyScreen(
  loadBillPaymentStoryTabs,
  "BillPaymentActivityRoute",
);
const BillPaymentOverviewRoute = lazyScreen(
  loadBillPaymentStoryTabs,
  "BillPaymentOverviewRoute",
);
const BillActivityRoute = lazyScreen(
  loadBillStoryTabs,
  "BillActivityRoute",
);
const BillLineItemsRoute = lazyScreen(
  loadBillStoryTabs,
  "BillLineItemsRoute",
);
const BillNotesRoute = lazyScreen(
  loadBillStoryTabs,
  "BillNotesRoute",
);
const BillOverviewRoute = lazyScreen(
  loadBillStoryTabs,
  "BillOverviewRoute",
);
const BillPaymentsRoute = lazyScreen(
  loadBillStoryTabs,
  "BillPaymentsRoute",
);
const BillRelatedRoute = lazyScreen(
  loadBillStoryTabs,
  "BillRelatedRoute",
);
const VendorCreditStoryDetail = lazyScreen(
  () => import("./routes/story/vendor-credit-story-detail"),
  "VendorCreditStoryDetail",
);
const VendorCreditActivityRoute = lazyScreen(
  loadVendorCreditStoryTabs,
  "VendorCreditActivityRoute",
);
const VendorCreditLineItemsRoute = lazyScreen(
  loadVendorCreditStoryTabs,
  "VendorCreditLineItemsRoute",
);
const VendorCreditOverviewRoute = lazyScreen(
  loadVendorCreditStoryTabs,
  "VendorCreditOverviewRoute",
);
const VendorCreditListPage = lazyScreen(
  () => import("./routes/vendor-credit-list"),
  "VendorCreditListPage",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "supplier-bills",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): BillListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: SupplierBillListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "supplier-bills/new",
    component: SupplierBillCreatePage,
  });

  const billPaymentsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "bill-payments",
    // POP-01 (ADR-0229): the record open in the pop-up, so a reload reopens
    // it and the browser's back button closes it.
    validateSearch: (raw: Record<string, unknown>): RecordPopupSearch => ({
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: BillPaymentListPage,
  });

  const vendorCreditsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "vendor-credits",
    // POP-01 (ADR-0229): as bill payments above.
    validateSearch: (raw: Record<string, unknown>): RecordPopupSearch => ({
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: VendorCreditListPage,
  });

  // ── UI-08: the two detail routes these lists have been pointing at nothing
  //    for. Both are story pages (ADR-0081), registered the same way the bill
  //    detail below is: a `$id` shell, an index redirect to …/overview, and
  //    one child per tab. ───────────────────────────────────────────────────
  const billPaymentDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "bill-payments/$id",
    validateSearch: (raw: Record<string, unknown>): BillPaymentStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: BillPaymentStoryDetail,
  });

  const billPaymentDetailIndexRoute = createRoute({
    getParentRoute: () => billPaymentDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/bill-payments/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const billPaymentOverviewRoute = createRoute({
    getParentRoute: () => billPaymentDetailRoute,
    path: "overview",
    component: BillPaymentOverviewRoute,
  });
  const billPaymentActivityRoute = createRoute({
    getParentRoute: () => billPaymentDetailRoute,
    path: "activity",
    component: BillPaymentActivityRoute,
  });

  (
    billPaymentDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    billPaymentDetailIndexRoute,
    billPaymentOverviewRoute,
    billPaymentActivityRoute,
  ]);

  const vendorCreditDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "vendor-credits/$id",
    validateSearch: (
      raw: Record<string, unknown>,
    ): VendorCreditStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: VendorCreditStoryDetail,
  });

  const vendorCreditDetailIndexRoute = createRoute({
    getParentRoute: () => vendorCreditDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/vendor-credits/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const vendorCreditOverviewRoute = createRoute({
    getParentRoute: () => vendorCreditDetailRoute,
    path: "overview",
    component: VendorCreditOverviewRoute,
  });
  const vendorCreditLineItemsRoute = createRoute({
    getParentRoute: () => vendorCreditDetailRoute,
    path: "line-items",
    component: VendorCreditLineItemsRoute,
  });
  const vendorCreditActivityRoute = createRoute({
    getParentRoute: () => vendorCreditDetailRoute,
    path: "activity",
    component: VendorCreditActivityRoute,
  });

  (
    vendorCreditDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    vendorCreditDetailIndexRoute,
    vendorCreditOverviewRoute,
    vendorCreditLineItemsRoute,
    vendorCreditActivityRoute,
  ]);

  // Edit (backlog B2): a focused FULL-PAGE form, deep-linkable, NOT nested
  // under the story shell. Parented directly to the module root so it renders
  // standalone (no StoryPageDetail header/tabs around it). Registered BEFORE
  // the `supplier-bills/$id` parent so the `/edit` literal segment is matched.
  const editRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "supplier-bills/$id/edit",
    component: BillEditPage,
  });

  // ── Story-page detail (ADR-0081): parent supplier-bills/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "supplier-bills/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): BillStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: BillStoryDetail,
  });

  // Index redirect supplier-bills/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/supplier-bills/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: BillOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: BillLineItemsRoute,
  });
  const paymentsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "payments",
    component: BillPaymentsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: BillRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: BillNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: BillActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      paymentsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [
    listRoute,
    createRouteEntry,
    editRoute,
    billPaymentsRoute,
    billPaymentDetailRoute,
    vendorCreditsRoute,
    vendorCreditDetailRoute,
    detailRoute,
  ];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
/** POP-01: a list whose only search parameter is the record open in its pop-up. */
export interface RecordPopupSearch {
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

export interface BillListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
